import { memo, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  Stack,
  TableCell,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Autocomplete,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ProductSearchInput from './ProductSearchInput';
import BarcodesInput from './BarcodesInput';
import { StockCells, CostCells } from './LineItemCells';
import {
  LINE_ITEM_COLUMN_COUNT,
  LINE_ITEM_DETAILS_BODY_SX,
  LINE_ITEM_DETAILS_GRID_SX,
} from './LineItemsTable';
import { LINE_INDEX_SX } from '../utils/lineItemCounter';
import { searchChangePatch } from '../utils/lineItems';
import { colors, formatCurrency } from '../constants';

const UNIT_OPTIONS = [
  'piece', 'each', 'box', 'case', 'pack', 'dozen', 'kg', 'g', 'lb', 'oz',
  'liter', 'ml', 'gallon', 'bottle', 'can', 'bag', 'roll', 'sheet', 'pair', 'set',
];

// How an existing product's stored cost (unitPrice) should be treated when it
// appears on this invoice line. Only shown/sent for existing (linked) products.
const COST_MODE_OPTIONS = [
  { value: 'keep', label: 'Keep existing cost' },
  { value: 'update', label: 'Update cost to this invoice' },
  { value: 'average_invoice', label: 'Average of invoice prices' },
  { value: 'average_balance', label: 'Average by balance' },
];

// Keep only digits and a single decimal point. A native `type="number"` input
// visually shows letters like "aa" while reporting an empty value to React, so
// numeric line-item fields use plain text inputs sanitized through this instead.
const sanitizeDecimal = (raw) => {
  const cleaned = String(raw).replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return (
    cleaned.slice(0, firstDot + 1) +
    cleaned.slice(firstDot + 1).replace(/\./g, '')
  );
};

// Fields that live in the collapsed section; an error on any of them opens it.
const ADVANCED_ERROR_KEYS = ['identity', 'sku', 'category'];

const MAIN_ROW_SX = { '& > td': { borderBottom: 'unset', verticalAlign: 'top' } };
const ADVANCED_CELL_SX = { py: 0, borderBottom: `1px solid ${colors.border}` };
const NAME_FIELD_SX = { gridColumn: { xs: 'span 12', md: 'span 4' } };
const SKU_FIELD_SX = { gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } };
const BARCODES_FIELD_SX = { gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 5' } };
const WIDE_FIELD_SX = { gridColumn: { xs: 'span 12', md: 'span 4' } };
const NARROW_FIELD_SX = { gridColumn: { xs: 'span 6', md: 'span 2' } };
const TOGGLE_CELL_SX = { whiteSpace: 'nowrap', pt: 1.25 };
const ITEM_CELL_SX = { overflow: 'hidden' };
const ITEM_NAME_SX = { fontWeight: 600, color: colors.navy };
const ITEM_SKU_SX = { color: colors.gray500, display: 'block' };
const NUMBER_INPUT_SX = { width: '100%' };
const DECIMAL_INPUT_PROPS = { inputMode: 'decimal', style: { textAlign: 'right' } };
const QUANTITY_INPUT_PROPS = { ...DECIMAL_INPUT_PROPS, 'aria-label': 'Quantity' };
const UNIT_COST_INPUT_PROPS = { ...DECIMAL_INPUT_PROPS, 'aria-label': 'Unit Cost' };
const PRICE_INPUT_PROPS = { ...DECIMAL_INPUT_PROPS, 'aria-label': 'Price' };
const SHRINK_LABEL_PROPS = { shrink: true };
const SKU_CONFLICT_LINK_SX = { font: 'inherit', verticalAlign: 'baseline' };

const getCategoryLabel = (option) => option?.name ?? '';
const isSameCategory = (option, value) => option.id === value.id;

/**
 * One editable line of a manually created purchase invoice, rendered as a
 * table row with its advanced fields in a collapsible row under it.
 *
 * Memoized on `item` and primitive preview props, so typing in one line leaves
 * the other lines untouched. That only holds while every callback prop is
 * stable, which is why the row addresses itself by `item.id` instead of by
 * index and why field patches that depend on the line's current value are
 * expressed as updater functions.
 */
function PurchaseInvoiceLineItemRow({
  item,
  categories,
  categoryById,
  onUpdate,
  onRemove,
  onItemSelected,
  onUnlink,
  onBarcodeScan,
  registerLineInput,
  previewStatus,
  previewNewCost,
  previewError,
}) {
  const { id } = item;
  const errors = item.errors || {};

  // A new item needs its Name, SKU and Menu Category filled in, so it starts
  // open; a linked item already has them.
  const [open, setOpen] = useState(!item.selectedItem);
  const hasAdvancedError = ADVANCED_ERROR_KEYS.some((key) => Boolean(errors[key]));
  useEffect(() => {
    if (hasAdvancedError) setOpen(true);
  }, [hasAdvancedError]);
  const toggleOpen = useCallback(() => setOpen((value) => !value), []);

  const searchInputRef = useCallback(
    (element) => registerLineInput(id, 'search', element),
    [registerLineInput, id],
  );
  const nameInputRef = useCallback(
    (element) => registerLineInput(id, 'name', element),
    [registerLineInput, id],
  );

  const handleRemove = useCallback(() => onRemove(id), [onRemove, id]);
  const handleUnlink = useCallback(() => onUnlink(id), [onUnlink, id]);
  const handleBarcodeScan = useCallback(
    () => onBarcodeScan(id),
    [onBarcodeScan, id],
  );

  const handleSearchChange = useCallback(
    (value) => onUpdate(id, (line) => searchChangePatch(line, value)),
    [onUpdate, id],
  );
  const handleItemSelected = useCallback(
    (found) => onItemSelected(id, found),
    [onItemSelected, id],
  );
  const handleLinkSkuConflict = useCallback(
    () => onItemSelected(id, item.skuConflict),
    [onItemSelected, id, item.skuConflict],
  );

  const handleNameChange = useCallback(
    (event) => {
      const name = event.target.value;
      onUpdate(id, (line) => ({
        name,
        errors: { ...(line.errors || {}), identity: undefined },
      }));
    },
    [onUpdate, id],
  );

  const handleSkuChange = useCallback(
    (event) => {
      const sku = event.target.value;
      onUpdate(id, (line) => ({
        sku,
        skuConflict: null,
        errors: {
          ...(line.errors || {}),
          identity: undefined,
          sku: undefined,
        },
      }));
    },
    [onUpdate, id],
  );

  const handleBarcodesChange = useCallback(
    (barcodes) => onUpdate(id, { barcodes }),
    [onUpdate, id],
  );

  const handleCategoryChange = useCallback(
    (event, category) => {
      onUpdate(id, (line) => ({
        categoryId: category?.id ?? null,
        categoryName: category?.name ?? null,
        errors: { ...(line.errors || {}), category: undefined },
      }));
    },
    [onUpdate, id],
  );

  const handleQuantityChange = useCallback(
    (event) => {
      const orderedQuantity = sanitizeDecimal(event.target.value);
      onUpdate(id, (line) => ({
        orderedQuantity,
        errors: { ...(line.errors || {}), orderedQuantity: undefined },
      }));
    },
    [onUpdate, id],
  );

  const handleUnitCostChange = useCallback(
    (event) => {
      const unitCost = sanitizeDecimal(event.target.value);
      onUpdate(id, (line) => ({
        unitCost,
        errors: { ...(line.errors || {}), unitCost: undefined },
      }));
    },
    [onUpdate, id],
  );

  const handleSellingPriceChange = useCallback(
    (event) => {
      const sellingPrice = sanitizeDecimal(event.target.value);
      onUpdate(id, (line) => ({
        sellingPrice,
        errors: { ...(line.errors || {}), sellingPrice: undefined },
      }));
    },
    [onUpdate, id],
  );

  const handleUnitChange = useCallback(
    (event) => onUpdate(id, { unit: event.target.value }),
    [onUpdate, id],
  );
  const handleExpiryChange = useCallback(
    (event) => onUpdate(id, { expiryDate: event.target.value }),
    [onUpdate, id],
  );
  const handleCostModeChange = useCallback(
    (event) => onUpdate(id, { costMode: event.target.value }),
    [onUpdate, id],
  );

  return (
    <>
      <TableRow sx={MAIN_ROW_SX}>
        <TableCell sx={TOGGLE_CELL_SX}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              size="small"
              onClick={toggleOpen}
              aria-expanded={open}
              aria-label="Toggle details"
            >
              {open ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
            <Typography variant="caption" sx={LINE_INDEX_SX} />
          </Stack>
        </TableCell>

        <TableCell sx={ITEM_CELL_SX}>
          {item.selectedItem ? (
            <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ pt: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={ITEM_NAME_SX} noWrap>
                  {item.name || item.selectedItem.name}
                </Typography>
                <Typography variant="caption" sx={ITEM_SKU_SX}>
                  {item.sku || item.selectedItem.sku}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <ProductSearchInput
              inputRef={searchInputRef}
              value={item.search}
              onChangeText={handleSearchChange}
              onItemSelected={handleItemSelected}
              productType="product"
              selectedItem={item.selectedItem}
              onClear={handleUnlink}
              onBarcodeScan={handleBarcodeScan}
            />
          )}
        </TableCell>

        <TableCell align="right">
          <TextField
            required
            value={item.orderedQuantity}
            onChange={handleQuantityChange}
            inputProps={QUANTITY_INPUT_PROPS}
            error={Boolean(errors.orderedQuantity)}
            helperText={errors.orderedQuantity || ''}
            size="small"
            sx={NUMBER_INPUT_SX}
          />
        </TableCell>

        <StockCells selectedItem={item.selectedItem} quantity={item.orderedQuantity} />

        <TableCell align="right">
          <TextField
            required
            value={item.unitCost}
            onChange={handleUnitCostChange}
            inputProps={UNIT_COST_INPUT_PROPS}
            error={Boolean(errors.unitCost)}
            helperText={errors.unitCost || ''}
            size="small"
            sx={NUMBER_INPUT_SX}
          />
        </TableCell>

        <CostCells
          selectedItem={item.selectedItem}
          quantity={item.orderedQuantity}
          unitCost={item.unitCost}
          previewStatus={previewStatus}
          previewNewCost={previewNewCost}
          previewError={previewError}
        />

        <TableCell align="right">
          <TextField
            required
            value={item.sellingPrice}
            onChange={handleSellingPriceChange}
            inputProps={PRICE_INPUT_PROPS}
            error={Boolean(errors.sellingPrice)}
            helperText={errors.sellingPrice || ''}
            size="small"
            sx={NUMBER_INPUT_SX}
          />
        </TableCell>

        <TableCell align="center">
          <IconButton size="small" color="error" onClick={handleRemove} aria-label="Remove">
            <CloseIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={LINE_ITEM_COLUMN_COUNT} sx={ADVANCED_CELL_SX}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={LINE_ITEM_DETAILS_BODY_SX}>
              <Box sx={LINE_ITEM_DETAILS_GRID_SX}>
                <Box sx={NAME_FIELD_SX}>
                  <TextField
                    label="Name"
                    inputRef={nameInputRef}
                    value={item.name}
                    onChange={handleNameChange}
                    error={Boolean(errors.identity)}
                    helperText={errors.identity || ''}
                    fullWidth
                    size="small"
                  />
                </Box>
                <Box sx={SKU_FIELD_SX}>
                  <TextField
                    label="SKU"
                    value={item.sku}
                    onChange={handleSkuChange}
                    error={Boolean(errors.identity || errors.sku)}
                    helperText={
                      errors.sku ? (
                        <>
                          {errors.sku}{' '}
                          {item.skuConflict && (
                            <Link
                              component="button"
                              type="button"
                              underline="always"
                              onClick={handleLinkSkuConflict}
                              sx={SKU_CONFLICT_LINK_SX}
                            >
                              Link this line to it
                            </Link>
                          )}
                        </>
                      ) : (
                        ''
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Box>
                <Box sx={BARCODES_FIELD_SX}>
                  <BarcodesInput
                    label="Barcodes"
                    value={item.barcodes}
                    onChange={handleBarcodesChange}
                  />
                </Box>
                <Box sx={WIDE_FIELD_SX}>
                  {item.selectedItem ? (
                    <TextField
                      label="Menu Category"
                      value={categoryById.get(item.categoryId)?.name ?? ''}
                      disabled
                      fullWidth
                      size="small"
                      helperText="Category of the existing product (read-only)."
                    />
                  ) : (
                    <Autocomplete
                      options={categories}
                      getOptionLabel={getCategoryLabel}
                      isOptionEqualToValue={isSameCategory}
                      value={categoryById.get(item.categoryId) ?? null}
                      onChange={handleCategoryChange}
                      fullWidth
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Menu Category"
                          required={item.productType === 'product'}
                          error={Boolean(errors.category)}
                          helperText={
                            errors.category ||
                            'New products sync to the menu under this category.'
                          }
                        />
                      )}
                    />
                  )}
                </Box>
                <Box sx={NARROW_FIELD_SX}>
                  <TextField
                    select
                    label="Unit"
                    value={item.unit}
                    onChange={handleUnitChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Select</MenuItem>
                    {UNIT_OPTIONS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={NARROW_FIELD_SX}>
                  <TextField
                    label="Expiry"
                    type="date"
                    InputLabelProps={SHRINK_LABEL_PROPS}
                    value={item.expiryDate}
                    onChange={handleExpiryChange}
                    fullWidth
                    size="small"
                  />
                </Box>
                {item.selectedItem && (
                  <Box sx={WIDE_FIELD_SX}>
                    <TextField
                      select
                      label="Cost Handling"
                      value={item.costMode || 'update'}
                      onChange={handleCostModeChange}
                      fullWidth
                      size="small"
                      helperText={
                        `Current cost: ${
                          item.selectedItem.unitPrice != null
                            ? formatCurrency(Number(item.selectedItem.unitPrice))
                            : 'n/a'
                        }` +
                        (item.selectedItem.lastInvoiceUnitPrice != null
                          ? ` \u00b7 Last invoice: ${formatCurrency(
                              Number(item.selectedItem.lastInvoiceUnitPrice),
                            )}`
                          : '')
                      }
                    >
                      {COST_MODE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default memo(PurchaseInvoiceLineItemRow);
